import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios"

const API_URL = process.env.API_URL
if (!API_URL) {
  throw new Error("Please provide the API_URL environment variable.")
}

export function getUploadURL(href: string) {
  return API_URL + href
}

// Retry configuration
const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY = 1000 // 1 second
const REQUEST_TIMEOUT = 30000 // 30 seconds

// Error codes that should trigger a retry (network-level errors)
const RETRYABLE_ERROR_CODES = [
  "ECONNRESET",
  "ECONNREFUSED",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ENETUNREACH",
  "EPIPE",
  "ERR_NETWORK",
]

// HTTP status codes that should trigger a retry
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504]

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retryCount?: number
}

function isRetryableError(error: AxiosError): boolean {
  if (error.code && RETRYABLE_ERROR_CODES.includes(error.code)) return true
  if (error.response?.status && RETRYABLE_STATUS_CODES.includes(error.response.status)) return true
  if (error.message?.includes("TLS") || error.message?.includes("socket disconnected")) return true
  return false
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const config: AxiosRequestConfig = {
  baseURL: API_URL,
  timeout: REQUEST_TIMEOUT,
}

const instance = axios.create(config)

// Add retry interceptor
instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as ExtendedAxiosRequestConfig | undefined
    if (!config) return Promise.reject(error)

    config._retryCount = config._retryCount ?? 0

    if (isRetryableError(error) && config._retryCount < MAX_RETRIES) {
      config._retryCount += 1
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, config._retryCount - 1)
      console.warn(
        `⏳ API retry ${config._retryCount}/${MAX_RETRIES} in ${delay}ms - ${
          error.code || error.message
        }`,
      )
      await sleep(delay)
      return instance(config)
    }

    console.error(`❌ API request failed: ${config.url} - ${error.code || error.message}`)
    return Promise.reject(error)
  },
)

export const setToken = (token: string) =>
  (instance.defaults.headers["Authorization"] = `Bearer ${token}`)

export const setLanguageHeader = (locale: "he" | "en") => {
  instance.defaults.headers["X-Accept-Language"] = locale === "he" ? `he-IL` : "en-US"
}

export const clearToken = () => delete instance.defaults.headers["Authorization"]

export default instance

export const CancelToken = axios.CancelToken
